import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  SelectChangeEvent
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { createGym } from '../../redux/gymSlice';
import { RootState, AppDispatch } from '../../redux/store';
import { useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import { LocationPicker } from '../../components/LocationPicker';
import { TimePicker } from '../../components/TimePicker';

const AMENITIES_OPTIONS = [
  'WiFi',
  'Parking',
  'Locker',
  'Shower',
  'AC',
  'Trainer',
  'Steam',
  'Sauna',
  'Cardio Equipment',
  'Weight Training',
  'Group Classes',
  'Swimming Pool',
  'Spa',
  'Cafe'
];

export const CreateGymPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { isLoading, error } = useSelector((state: RootState) => state.gym);
  const [successMessage, setSuccessMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    latitude: 0,
    longitude: 0,
    phone: '',
    email: '',
    opening_time: '06:00:00',
    closing_time: '22:00:00',
    total_capacity: '',
    amenities: [] as string[]
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (fieldErrors[name]) {
      setFieldErrors({ ...fieldErrors, [name]: '' });
    }
  };

  const handleAmenitiesChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setFormData({ ...formData, amenities: typeof value === 'string' ? value.split(',') : value });
  };

  const handleLocationSelect = (location: { lat: number; lng: number; address: string }) => {
    setFormData({
      ...formData,
      latitude: location.lat,
      longitude: location.lng,
      address: location.address
    });
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.name || formData.name.trim().length < 3) {
      errors.name = 'Gym name must be at least 3 characters';
    }

    if (!formData.address || formData.address.trim().length < 10) {
      errors.address = 'Address must be at least 10 characters';
    }

    if (!formData.city || formData.city.trim().length < 2) {
      errors.city = 'City is required';
    }

    if (!formData.state || formData.state.trim().length < 2) {
      errors.state = 'State is required';
    }

    if (!formData.pincode || !/^[0-9]{6}$/.test(formData.pincode)) {
      errors.pincode = 'Pincode must be 6 digits';
    }

    if (formData.phone && !/^[0-9]{10}$/.test(formData.phone)) {
      errors.phone = 'Phone must be 10 digits';
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }

    const capacity = parseInt(formData.total_capacity);
    if (!formData.total_capacity || isNaN(capacity) || capacity < 1) {
      errors.total_capacity = 'Capacity must be at least 1';
    }

    if (formData.latitude === 0 || formData.longitude === 0) {
      errors.location = 'Please select location on map';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const gymData = {
      ...formData,
      total_capacity: parseInt(formData.total_capacity)
    };

    const result = await dispatch(createGym(gymData));
    
    if (result.type === createGym.fulfilled.type) {
      setSuccessMessage('Gym created successfully!');
      setTimeout(() => {
        navigate('/gyms');
      }, 1500);
    }
  };

  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/gyms')}
          sx={{ mb: 2 }}
        >
          Back to Gyms
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
          Add New Gym
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Fill in the details to create a new gym location
        </Typography>
      </Box>

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={4}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, pb: 1, borderBottom: '2px solid #14B8A6' }}>
              Basic Information
            </Typography>

              <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Gym Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    error={!!fieldErrors.name}
                    helperText={fieldErrors.name}
                    placeholder="e.g., Gold's Gym - Banjara Hills"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    multiline
                    rows={3}
                    placeholder="Describe your gym facilities and features"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    error={!!fieldErrors.phone}
                    helperText={fieldErrors.phone}
                    placeholder="10-digit phone number"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    error={!!fieldErrors.email}
                    helperText={fieldErrors.email}
                    placeholder="gym@example.com"
                  />
                </Grid>
              </Grid>
          </Grid>

          {/* Location Details */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, pb: 1, borderBottom: '2px solid #14B8A6' }}>
              Location Details
            </Typography>

              {fieldErrors.location && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {fieldErrors.location}
                </Alert>
              )}

              <Box sx={{ mb: 3 }}>
                <LocationPicker
                  onLocationSelect={handleLocationSelect}
                  apiKey={googleMapsApiKey}
                />
              </Box>

              <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    required
                    multiline
                    rows={2}
                    error={!!fieldErrors.address}
                    helperText={fieldErrors.address}
                    placeholder="Complete address"
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="City"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    required
                    error={!!fieldErrors.city}
                    helperText={fieldErrors.city}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="State"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    required
                    error={!!fieldErrors.state}
                    helperText={fieldErrors.state}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Pincode"
                    name="pincode"
                    value={formData.pincode}
                    onChange={handleChange}
                    required
                    error={!!fieldErrors.pincode}
                    helperText={fieldErrors.pincode}
                    placeholder="6-digit pincode"
                  />
                </Grid>
              </Grid>
          </Grid>

          {/* Operating Hours & Capacity */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, pb: 1, borderBottom: '2px solid #14B8A6' }}>
              Operating Hours & Capacity
            </Typography>

              <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
                <Grid item xs={12} sm={6}>
                  <TimePicker
                    label="Opening Time"
                    value={formData.opening_time}
                    onChange={(value) => setFormData({ ...formData, opening_time: value })}
                    required
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TimePicker
                    label="Closing Time"
                    value={formData.closing_time}
                    onChange={(value) => setFormData({ ...formData, closing_time: value })}
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Total Capacity"
                    name="total_capacity"
                    type="number"
                    value={formData.total_capacity}
                    onChange={handleChange}
                    required
                    error={!!fieldErrors.total_capacity}
                    helperText={fieldErrors.total_capacity}
                    placeholder="Maximum number of members"
                    inputProps={{ min: 1 }}
                  />
                </Grid>
              </Grid>
          </Grid>

          {/* Amenities */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, pb: 1, borderBottom: '2px solid #14B8A6' }}>
              Amenities
            </Typography>

              <FormControl fullWidth>
                <InputLabel>Select Amenities</InputLabel>
                <Select
                  multiple
                  value={formData.amenities}
                  onChange={handleAmenitiesChange}
                  input={<OutlinedInput label="Select Amenities" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {AMENITIES_OPTIONS.map((amenity) => (
                    <MenuItem key={amenity} value={amenity}>
                      {amenity}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexDirection: { xs: 'column', sm: 'row' } }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/gyms')}
                sx={{ height: '48px', minWidth: { xs: '100%', sm: '140px' }, order: { xs: 2, sm: 1 } }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                type="submit"
                disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <SaveIcon />}
                sx={{
                  height: '48px',
                  minWidth: { xs: '100%', sm: '160px' },
                  backgroundColor: '#14B8A6',
                  '&:hover': {
                    backgroundColor: '#0F9688'
                  },
                  order: { xs: 1, sm: 2 }
                }}
              >
                {isLoading ? 'Creating...' : 'Create Gym'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Container>
  );
};

export default CreateGymPage;
